<script setup>
import { ref, inject } from 'vue';
import { Head } from '@inertiajs/vue3';
import { useUserStore } from '@/stores/user';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';
import { FOODFLOW_MODAL_CARD_CLASS } from '@/utils/foodFlowModal';
import FoodFlowPage from '@/components/FoodFlow/FoodFlowPage.vue';
import FoodFlowBadge from '@/components/FoodFlow/FoodFlowBadge.vue';

const { search } = useUserStore();
const { all } = storeToRefs(useUserStore());
const { roleOptions } = storeToRefs(useOptionsStore());

const params = ref({
    term: '',
    status: null,
    page: 1,
    sort_by: null,
    per_page: 10,
    role: null,
});

const searchUser = () => {
    search({
        ...params.value,
    });
};
searchUser();

const goToPage = (page) => {
    params.value.page = page;
    searchUser();
};

const modals = inject('modals');

const openModal = (mode = 'Create', user = null) => {
    modals.show('UsersModal', {
        props: {
            bodyClass: FOODFLOW_MODAL_CARD_CLASS,
            ...(user ? { user, mode } : { mode }),
        }
    }).then(() => {
        searchUser();
    });
};
</script>

<template>
<FoodFlowPage max-width-class="max-w-[1000px]">
    <Head :title="$t('label.users')" />
    <template #title>{{ $t('label.users') }}</template>
    <template #actions>
        <button type="button" class="foodflow-btn-primary" @click="openModal('Create')"><FontAwesomeIcon icon="plus" class="mr-1" />{{ $t('label.new_user') }}</button>
    </template>

    <div class="foodflow-filter-bar">
        <div class="search-group">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span class="text-[14px] text-[#94a3b8]">🔍</span>
            <StyledSearch
                v-model="params.term"
                v-debounce:500ms="searchUser"
                name="search"
                :placeholder="$t('label.search_users')"
                :debounce-events="['input']" />
        </div>
        <div class="foodflow-filter-group">
            <span class="foodflow-filter-label">{{ $t('label.role') }}</span>
            <select v-model="params.role" class="foodflow-filter-select" @change="searchUser">
                <option :value="null">{{ $t('label.all') }}</option>
                <option v-for="role in roleOptions" :key="role.name" :value="role.name">{{ role.display_name }}</option>
            </select>
        </div>
        <div class="foodflow-filter-group">
            <span class="foodflow-filter-label">{{ $t('label.status') }}</span>
            <select v-model="params.status" class="foodflow-filter-select" @change="searchUser">
                <option :value="null">{{ $t('label.all') }}</option>
                <option :value="true">{{ $t('label.active') }}</option>
                <option :value="false">{{ $t('label.inactive') }}</option>
            </select>
        </div>
    </div>

    <div class="foodflow-table">
        <div class="foodflow-table-header users-table-grid">
            <div class="foodflow-table-header-cell">{{ $t('label.user') }}</div>
            <div class="foodflow-table-header-cell">{{ $t('label.status') }}</div>
            <div class="foodflow-table-header-cell hidden sm:block">{{ $t('label.role') }}</div>
            <div />
        </div>
        <template v-if="all?.data?.length">
            <div
                v-for="user in all.data"
                :key="user.hash"
                class="foodflow-table-row users-table-grid">
                <div class="users-table-cell-name">
                    <div class="users-table-cell-name-primary">{{ user.first_name }} {{ user.last_name }}</div>
                    <div class="users-table-cell-name-secondary" :title="user.email">{{ user.email }}</div>
                </div>
                <div class="foodflow-table-cell">
                    <FoodFlowBadge :variant="user.is_active ? 'green' : 'gray'">
                        {{ user.is_active ? $t('label.active') : $t('label.inactive') }}
                    </FoodFlowBadge>
                </div>
                <div class="users-table-cell-role">
                    <FoodFlowBadge v-if="user.roles.length" variant="navy">{{ user.roles[0].display_name }}</FoodFlowBadge>
                </div>
                <div class="foodflow-table-cell-actions">
                    <button type="button" class="foodflow-btn-muted" @click="openModal('Edit', user)">{{ $t('label.edit') }}</button>
                </div>
            </div>
        </template>
        <div v-else class="foodflow-table-empty">
            {{ $t('label.no_matches_found') }}
        </div>
    </div>

    <div v-if="all?.last_page > 1" class="foodflow-pagination">
        <button
            type="button"
            class="foodflow-btn-muted"
            :disabled="all.current_page <= 1"
            @click="goToPage(all.current_page - 1)">{{ $t('label.previous') }}</button>
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span class="text-[12px] text-[#64748b]">{{ all.current_page }} / {{ all.last_page }}</span>
        <button
            type="button"
            class="foodflow-btn-muted"
            :disabled="all.current_page >= all.last_page"
            @click="goToPage(all.current_page + 1)">{{ $t('label.next') }}</button>
    </div>
</FoodFlowPage>
</template>
